import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Drawer, ErrorState, HistoryIcon, LoadingState } from "@signet/ui";
import { dayText, exKeys, fetchMonthly, fetchPayments, price, type Month, type Payment } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The top of the page (Jay, 2026-10-21): a line of the total spent each
   month, in AUD at each payment's own day's rate. History swaps the line for
   every payment, newest first; clicking one opens it in a sidebar with its
   notes and the item's.
   ────────────────────────────────────────────────────────────────────────── */

const monthLabel = (month: string, long = false) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString("en-AU", long ? { month: "long", year: "numeric" } : { month: "short" });

const aud = (n: number) => price(n, "AUD");
const audShort = (n: number) => (n >= 1000 ? `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K` : `$${Math.round(n)}`);

/** Steps a person would write on an axis: $0, $500, $1K, $1.5K. */
function ticksFor(peak: number): number[] {
  if (peak <= 0) return [0, 100];
  const raw = peak / 3;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((s) => s >= raw) ?? raw;
  const ticks: number[] = [];
  for (let t = 0; t <= peak + step * 0.999; t += step) ticks.push(t);
  if (ticks.at(-1)! < peak) ticks.push(ticks.at(-1)! + step);
  return ticks;
}

function MonthlyChart({ months }: { months: Month[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const ticks = ticksFor(Math.max(...months.map((m) => m.aud)));
  const top = ticks.at(-1)!;
  const x = (i: number) => (months.length > 1 ? (i / (months.length - 1)) * 100 : 50);
  const y = (v: number) => ((top - v) / top) * 100;
  // Straight lines between months: spending is never below zero, and a curve between two empty months dips under it.
  const line = months.map((m, i) => `${i ? "L" : "M"} ${x(i)} ${y(m.aud)}`).join(" ");
  const active = hovered === null ? null : months[hovered]!;

  return (
    <div className="ex-chart">
      <div className="ex-chart-axis" aria-hidden="true">
        {[...ticks].reverse().map((t) => <span key={t} style={{ top: `${y(t)}%` }}>{audShort(t)}</span>)}
      </div>
      <div
        className="ex-chart-plot"
        onPointerMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const fraction = box.width ? (event.clientX - box.left) / box.width : 0;
          setHovered(Math.max(0, Math.min(months.length - 1, Math.round(fraction * (months.length - 1)))));
        }}
        onPointerLeave={() => setHovered(null)}
      >
        {ticks.map((t) => <i key={t} className="ex-chart-grid" style={{ top: `${y(t)}%` }} />)}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={`Total spent each month, ${monthLabel(months[0]!.month, true)} to ${monthLabel(months.at(-1)!.month, true)}`}>
          <defs>
            <linearGradient id="ex-chart-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity=".22" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${line} L 100 100 L 0 100 Z`} fill="url(#ex-chart-fill)" />
          <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        {months.map((m, i) => <i key={m.month} className={`ex-chart-dot${hovered === i ? " on" : ""}`} style={{ left: `${x(i)}%`, top: `${y(m.aud)}%` }} />)}
        {active && hovered !== null ? (
          <>
            <i className="ex-chart-cross" style={{ left: `${x(hovered)}%` }} />
            <div className={`ex-chart-tip${x(hovered) > 70 ? " left" : ""}`} style={{ left: `${x(hovered)}%` }}>
              <span>{monthLabel(active.month, true)}</span>
              <strong>{aud(active.aud)}</strong>
              {active.missing ? <small>{active.missing === 1 ? "1 payment" : `${active.missing} payments`} waiting for an exchange rate</small> : null}
            </div>
          </>
        ) : null}
      </div>
      <div className="ex-chart-months" aria-hidden="true">
        {months.map((m, i) => <span key={m.month} style={{ left: `${x(i)}%` }}>{monthLabel(m.month)}</span>)}
      </div>
    </div>
  );
}

function History({ onOpen }: { onOpen: (p: Payment) => void }) {
  const list = useQuery({ queryKey: exKeys.payments, queryFn: fetchPayments });
  if (list.isLoading) return <LoadingState />;
  if (list.isError) return <ErrorState title="Couldn't load the payments" onRetry={() => list.refetch()} />;
  const payments = list.data?.payments ?? [];
  if (!payments.length) return <p className="ex-quiet">No payments yet. Marking a line item paid adds one here.</p>;
  return (
    <div className="table ex-history">
      <div className="table-row head ex-hist-row">
        <span>Paid</span>
        <span>Line item</span>
        <span className="num">Amount</span>
      </div>
      {payments.map((p) => (
        <button key={p.id} type="button" className="table-row ex-hist-row" onClick={() => onOpen(p)}>
          <span className="ex-hist-day">{dayText(p.paidOn)}</span>
          <span className="table-cell">
            <strong>{p.itemName}</strong>
            {p.category ? <small>{p.category}</small> : null}
          </span>
          <span className="num ex-hist-amount">
            {price(p.amount, p.currency)}
            {p.currency !== "AUD" && p.amountAud !== null ? <small>{aud(p.amountAud)}</small> : null}
          </span>
        </button>
      ))}
    </div>
  );
}

function PaymentDrawer({ payment, onClose }: { payment: Payment | null; onClose: () => void }) {
  return (
    <Drawer open={!!payment} onClose={onClose} title={payment?.itemName ?? ""} subtitle={payment ? `Paid ${dayText(payment.paidOn)}` : undefined} narrow>
      {payment ? (
        <div className="ex-pay">
          <dl className="ex-pay-facts">
            <div><dt>Amount</dt><dd>{price(payment.amount, payment.currency)}{payment.currency !== "AUD" && payment.amountAud !== null ? <small> ({aud(payment.amountAud)})</small> : null}</dd></div>
            {payment.category ? <div><dt>Category</dt><dd>{payment.category}</dd></div> : null}
            {payment.expiryOn ? <div><dt>Expiry it paid</dt><dd>{dayText(payment.expiryOn)}</dd></div> : null}
            {payment.nextExpiry ? <div><dt>Next expiry set to</dt><dd>{dayText(payment.nextExpiry)}</dd></div> : null}
            {payment.paidBy ? <div><dt>Marked paid by</dt><dd>{payment.paidBy}</dd></div> : null}
          </dl>
          <section>
            <h3>Payment notes</h3>
            {payment.notes ? <p className="ex-pay-notes">{payment.notes}</p> : <p className="faint">None.</p>}
          </section>
          <section>
            <h3>Line item notes</h3>
            {payment.itemNotes ? <p className="ex-pay-notes">{payment.itemNotes}</p> : <p className="faint">{payment.itemId ? "None." : "The line item has been removed."}</p>}
          </section>
        </div>
      ) : null}
    </Drawer>
  );
}

export function SpendPanel() {
  const [history, setHistory] = useState(false);
  const [open, setOpen] = useState<Payment | null>(null);
  const monthly = useQuery({ queryKey: exKeys.monthly, queryFn: fetchMonthly });

  return (
    <section className="panel ex-spend">
      <div className="panel-head ex-spend-head">
        <div>
          <h2>{history ? "Every payment" : "Total spent each month"}</h2>
          <p>{history ? "Newest first. Click one for its notes." : "In AUD, at the exchange rate on the day each was paid."}</p>
        </div>
        <button type="button" className={`secondary-button compact${history ? " on" : ""}`} aria-pressed={history} onClick={() => setHistory(!history)}>
          <HistoryIcon size={13} /> History
        </button>
      </div>
      <div className={`panel-body${history ? " flush" : ""}`}>
        {history ? (
          <History onOpen={setOpen} />
        ) : monthly.isLoading ? (
          <LoadingState />
        ) : monthly.isError || !monthly.data ? (
          <ErrorState title="Couldn't load the monthly totals" onRetry={() => monthly.refetch()} />
        ) : (
          <MonthlyChart months={monthly.data.months} />
        )}
      </div>
      <PaymentDrawer payment={open} onClose={() => setOpen(null)} />
    </section>
  );
}
